/* 2026-10-07 (shipped from mock H1): the LED Staff, lit live by the mode you pick, in the light modes section.
   Joel: "lets have the sabers on the product pages so you can interact with them in the app demo
   section". Each end of the staff is its own Critter, so each end can run its own mode.

   The staff is three real Cycles layers (export_web.py): the unlit staff, and each end's light
   (tube + Silicone Saber Cap + halo) for a unit white LED. Per frame the page does the same math as
   compose_glow.py, so it matches the gallery renders:
     linear = base + cL * E_L + cR * E_R,  then exposure, Khronos PBR Neutral, sRGB, edge feather
   cL / cR come from window.AOAM_CA_CORE (the section's own firmware tables and timing).

   Flash safety: a whole staff is a much bigger lit area than the ring's small bulb, so any mode
   that cuts hard more than 6 times a second (3 flashes) plays slowed down to that rate, and says
   so. Pause motion and prefers-reduced-motion show a still. */
(function () {
  'use strict';

  /* sc: fit the layer inside the canvas without stretching it (contain); outside it is black */
  var VS = 'attribute vec2 p; uniform vec2 sc; varying vec2 uv; void main(){ uv = (vec2(p.x * .5 + .5, .5 - p.y * .5) - .5) * sc + .5; gl_Position = vec4(p, 0., 1.); }';
  var FS = [
    'precision highp float;',
    'uniform sampler2D tB, tL, tR; uniform vec3 lm; uniform float k, ex; uniform vec3 cL, cR; uniform vec2 fe;',
    'varying vec2 uv;',
    'vec3 dec(sampler2D t, float m){ return k * (exp(texture2D(t, uv).rgb * m) - 1.); }',
    'vec3 neutral(vec3 c){',
    '  float x = min(c.r, min(c.g, c.b));',
    '  float off = x < .08 ? x - 6.25 * x * x : .04;',
    '  c -= off;',
    '  float peak = max(c.r, max(c.g, c.b));',
    '  const float sc = .76; const float d = .24;',
    '  if (peak < sc) return c;',
    '  float np = 1. - d * d / (peak + d - sc);',
    '  c *= np / peak;',
    '  float g = 1. - 1. / (.15 * (peak - np) + 1.);',
    '  return mix(c, vec3(np), g);',
    '}',
    'vec3 oetf(vec3 c){ c = clamp(c, 0., 1.); return mix(12.92 * c, 1.055 * pow(c, vec3(1. / 2.4)) - .055, step(.0031308, c)); }',
    'void main(){',
    '  vec3 l = dec(tB, lm.x) + cL * dec(tL, lm.y) + cR * dec(tR, lm.z);',
    '  vec3 o = oetf(neutral(l * ex));',
    '  vec2 q = min(uv, 1. - uv) / fe; float r = clamp(min(q.x, q.y), 0., 1.);',
    '  gl_FragColor = vec4(o * r * r * (3. - 2. * r), 1.);',
    '}'].join('\n');

  function lin(c) {                       /* LED byte (0-255, sRGB coded like the app) -> linear */
    return c.map(function (v) { v /= 255; return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); });
  }

  function Staff(canvas, layers, man) {
    this.cv = canvas; this.man = man; this.ok = false;
    var gl = canvas.getContext('webgl2', { antialias: false, premultipliedAlpha: false }) ||
             canvas.getContext('webgl', { antialias: false, premultipliedAlpha: false });
    if (!gl) return;
    this.gl = gl; this.gl2 = typeof WebGL2RenderingContext !== 'undefined' && gl instanceof WebGL2RenderingContext;
    function sh(type, src) { var s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s)); return s; }
    var pr = gl.createProgram();
    gl.attachShader(pr, sh(gl.VERTEX_SHADER, VS)); gl.attachShader(pr, sh(gl.FRAGMENT_SHADER, FS));
    gl.linkProgram(pr); gl.useProgram(pr); this.pr = pr;
    var b = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, b);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    var loc = gl.getAttribLocation(pr, 'p'); gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    var U = this.U = {};
    ['tB', 'tL', 'tR', 'lm', 'k', 'ex', 'cL', 'cR', 'fe', 'sc'].forEach(function (n) { U[n] = gl.getUniformLocation(pr, n); });
    var lg = function (M) { return Math.log(1 + M / man.k); };
    gl.uniform3f(U.lm, lg(man.M.base), lg(man.M.L), lg(man.M.R));
    gl.uniform1f(U.k, man.k); gl.uniform1f(U.ex, Math.pow(2, man.exposure));
    var F = 0.0275 * Math.max(man.w, man.h);                 /* the gallery renders' 2.75% feather */
    gl.uniform2f(U.fe, F / man.w, F / man.h);
    gl.uniform1i(U.tB, 0); gl.uniform1i(U.tL, 1); gl.uniform1i(U.tR, 2);
    var self = this, left = 3;
    ['base', 'L', 'R'].forEach(function (n, i) {
      var img = new Image();
      img.onload = function () {
        gl.activeTexture(gl.TEXTURE0 + i);
        var t = gl.createTexture(); gl.bindTexture(gl.TEXTURE_2D, t);
        gl.pixelStorei(gl.UNPACK_COLORSPACE_CONVERSION_WEBGL, gl.NONE);   /* raw bytes, no color management */
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, img);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        if (self.gl2) { gl.generateMipmap(gl.TEXTURE_2D); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR); }
        else gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        if (--left === 0) { self.ok = true; if (self.onready) self.onready(); }
      };
      var url = typeof layers === 'string' ? layers + '_' + n + '.png' : layers[n];
      /* Shopify's CDN hands an <img> (Accept: image/webp) a LOSSY webp of these PNGs, which smears the
         log-coded light. fetch() asks for image/png and gets the PNG, pixel for pixel (checked 2026-10-07). */
      if (window.fetch && window.URL && URL.createObjectURL) {
        fetch(url, { headers: { Accept: 'image/png' } }).then(function (r) { if (!r.ok) throw 0; return r.blob(); })
          .then(function (b) { img.src = URL.createObjectURL(b); }, function () { img.src = url; });
      } else img.src = url;
    });
  }
  Staff.prototype.draw = function (a, b) {
    if (!this.ok) return;
    var gl = this.gl, cv = this.cv, dpr = Math.min(2, window.devicePixelRatio || 1);
    var w = Math.max(1, Math.round(cv.clientWidth * dpr)), h = Math.max(1, Math.round(cv.clientHeight * dpr));
    if (cv.width !== w || cv.height !== h) { cv.width = w; cv.height = h; }
    gl.viewport(0, 0, w, h);
    var ca = w / h, ta = this.man.w / this.man.h;
    gl.uniform2f(this.U.sc, ca > ta ? ca / ta : 1, ca > ta ? 1 : ta / ca);
    var A = lin(a), B = lin(b);
    gl.uniform3f(this.U.cL, A[0], A[1], A[2]); gl.uniform3f(this.U.cR, B[0], B[1], B[2]);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  };

  /* hard cuts per second at the firmware's own speed -> how much to slow it for <= 3 flashes/s */
  function slow(sq) {
    var S = sq.S, cuts = 0, i;
    for (i = 0; i < S.length; i++) {
      var prev = S[(i - 1 + S.length) % S.length];
      if (S.length > 1 && S[i].a !== prev.b) cuts++;
    }
    return Math.max(1, (cuts / (sq.total / 1000)) / 6);
  }
  function stillColor(core, sq) {               /* for Pause motion: the first lit moment of the mode */
    for (var i = 0; i < sq.S.length; i++) {
      var c = core.seqRGB(sq, sq.S[i].t0 + sq.S[i].ms * 0.5);
      if (c[0] + c[1] + c[2] > 9) return c;
    }
    return [0, 0, 0];
  }

  /* opts: { root, canvas, layers, manifest, names: ['Upper', 'Lower'], ends (the switch), info, tag, hint } */
  window.AOAM_StaffDemo = function (o) {
    var core = window.AOAM_CA_CORE; if (!core) return;
    var st = new Staff(o.canvas, o.layers, o.manifest);
    if (!st.gl) { o.canvas.parentNode.classList.add('st-nogl'); return; }
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var stillBtn = o.root.querySelector('[data-still]');
    var end = 'both', id = ['dm9', 'dm9'], sq = [core.seq('dm9'), core.seq('dm9')], t = [0, 0], scale = 1;
    var raf = 0, last = 0, vis = false;
    function paused() { return reduce || (stillBtn && stillBtn.getAttribute('aria-pressed') === 'true'); }
    function rescale() {
      scale = Math.max(slow(sq[0]), slow(sq[1]));
      if (o.tag) o.tag.hidden = !(scale > 1.05) || paused();
    }
    function label() {
      if (!o.info) return;
      if (id[0] === id[1]) { o.info.hidden = true; return; }
      o.info.hidden = false;
      o.info.innerHTML = '<span><b>' + o.names[0] + '</b> ' + core.def(id[0]).n + '</span><span><b>' + o.names[1] + '</b> ' + core.def(id[1]).n + '</span>';
    }
    function setEnd(e) {
      end = e;
      o.ends.querySelectorAll('[data-end]').forEach(function (b) { b.setAttribute('aria-pressed', String(b.getAttribute('data-end') === e)); });
      if (o.hint) o.hint.textContent = e === 'both' ? 'Tap an end to give it its own mode'
        : 'Now pick a mode for the ' + o.names[e === 'L' ? 0 : 1].toLowerCase() + ' end';
    }
    function frame(now) {
      raf = 0;
      var dt = Math.min(64, now - (last || now)); last = now;
      t[0] += dt / scale; t[1] += dt / scale;
      if (paused()) { st.draw(stillColor(core, sq[0]), stillColor(core, sq[1])); return; }
      st.draw(core.seqRGB(sq[0], t[0]), core.seqRGB(sq[1], t[1]));
      if (vis && !document.hidden) raf = requestAnimationFrame(frame);
    }
    function wake() { if (!raf) { last = 0; raf = requestAnimationFrame(frame); } }
    st.onready = wake;
    o.root.addEventListener('lx:hero', function (e) {
      var n = e.detail.id;
      if (end !== 'R') { id[0] = n; sq[0] = core.seq(n); t[0] = 0; }
      if (end !== 'L') { id[1] = n; sq[1] = core.seq(n); t[1] = 0; }
      if (end === 'both') t = [0, 0];               /* both ends on one mode run in sync, as the app does */
      rescale(); label(); wake();
    });
    o.ends.addEventListener('click', function (e) {
      var b = e.target.closest('[data-end]'); if (b) setEnd(b.getAttribute('data-end'));
    });
    /* tap an end of the staff itself to pick it; tap it again (or the middle) for both */
    o.canvas.addEventListener('click', function (e) {
      var r = o.canvas.getBoundingClientRect(), x = (e.clientX - r.left) / r.width, y = (e.clientY - r.top) / r.height;
      var s = o.axis(x, y);                       /* -1 .. 1 along the staff, left/upper end first */
      var pick = s < -0.25 ? 'L' : s > 0.25 ? 'R' : 'both';
      setEnd(pick === end ? 'both' : pick);
    });
    if (stillBtn) stillBtn.addEventListener('click', function () { setTimeout(function () { rescale(); wake(); }, 0); });
    if ('IntersectionObserver' in window) new IntersectionObserver(function (en) { vis = en[0].isIntersecting; if (vis) wake(); }).observe(o.canvas);
    else vis = true;
    document.addEventListener('visibilitychange', function () { if (!document.hidden) wake(); });
    addEventListener('resize', wake);
    rescale(); setEnd('both');
  };
})();
